/**
 * G3 (Counsel 2026-10-22): what a consent answer does in the page right now,
 * with no reload.
 *
 * Opt-out (Turn off analytics, Decline, or analytics switched off in Your
 * privacy choices), in this order:
 *   1. ClarityRecorder hears CLARITY_ANALYTICS_OFF_EVENT and stops recording.
 *   2. Google Consent Mode update with all four keys denied.
 *   3. clarity('consentv2') with analytics and ads denied.
 *   4. The GA4, Clarity, Meta Pixel and Google Ads cookies are deleted on this
 *      host and on the parent domain.
 *   5. The Google tag goes quiet for the rest of the page (stopGoogleTag): the
 *      GA4 property's ga-disable switch, gtag a no-op, dataLayer.push a no-op.
 *      Consent Mode advanced would otherwise keep sending cookieless pings
 *      (measurement on live #970, 2026-10-23). The next page load opens with
 *      the stored decline, so no Google tag loads at all (googleTagBlockedJs).
 * The server reads analytics:false from the consent cookie, so its level turns
 * to declined on the next request, and the Pixel never loads (it needs
 * marketing:true).
 *
 * Grant (Got it, Accept analytics, Save choices with analytics on): analytics
 * granted, ad_* granted only when marketing is on.
 *
 * Window and document are parameters so the tests drive a fake page.
 */

import { CLARITY_ANALYTICS_OFF_EVENT } from '@/lib/analytics/clarity'

export const CONSENT_SITE_DOMAIN = '.ryan-realty.com'

/** Exact cookie names the opt-out deletes. */
export const OPT_OUT_COOKIE_NAMES = ['_ga', '_clck', '_clsk', '_fbp'] as const
/** Cookie name prefixes the opt-out deletes (_ga_<container>, _gcl_au, _gcl_aw...). */
export const OPT_OUT_COOKIE_PREFIXES = ['_ga_', '_gcl_'] as const

type ConsentValue = 'granted' | 'denied'

export type ConsentModeUpdate = {
  analytics_storage: ConsentValue
  ad_storage: ConsentValue
  ad_user_data: ConsentValue
  ad_personalization: ConsentValue
}

/** The GA4 property GTM's Google tag sends to (GTM-WV6R4NZ5 -> G-ST40W4WM6T). */
const GA4_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID?.trim() || 'G-ST40W4WM6T'

type ConsentWindow = {
  gtag?: (...args: unknown[]) => void
  dataLayer?: unknown[]
  clarity?: (...args: unknown[]) => void
  dispatchEvent?: (event: Event) => boolean
  location?: { hostname?: string }
}

type ConsentDocument = { cookie: string }

export const CONSENT_MODE_DENIED: ConsentModeUpdate = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
}

export function consentModeUpdate(choice: { analytics: boolean; marketing: boolean }): ConsentModeUpdate {
  const ad: ConsentValue = choice.marketing ? 'granted' : 'denied'
  return {
    analytics_storage: choice.analytics ? 'granted' : 'denied',
    ad_storage: ad,
    ad_user_data: ad,
    ad_personalization: ad,
  }
}

function sendConsentMode(win: ConsentWindow, update: ConsentModeUpdate) {
  if (typeof win.gtag === 'function') {
    win.gtag('consent', 'update', update)
    return
  }
  // gtag not defined yet: queue the same command the way gtag.js reads it.
  if (!Array.isArray(win.dataLayer)) win.dataLayer = []
  // eslint-disable-next-line prefer-rest-params -- gtag.js reads Arguments objects, mirror its shape
  win.dataLayer.push((function (..._args: unknown[]) { return arguments })('consent', 'update', update))
}

function sendClarity(win: ConsentWindow, choice: { analytics: boolean; marketing: boolean }) {
  if (typeof win.clarity !== 'function') return
  try {
    win.clarity('consentv2', {
      analytics_Storage: choice.analytics ? 'granted' : 'denied',
      ad_Storage: choice.marketing ? 'granted' : 'denied',
    })
  } catch {
    /* Clarity not ready */
  }
}

/** Names of the tracking cookies present in document.cookie. */
export function trackingCookieNames(cookieHeader: string): string[] {
  const names = cookieHeader
    .split(';')
    .map((part) => part.trim().split('=')[0])
    .filter(Boolean)
  return names.filter(
    (name) =>
      (OPT_OUT_COOKIE_NAMES as readonly string[]).includes(name) ||
      OPT_OUT_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix)),
  )
}

/** Ad cookies only (Meta Pixel, Google Ads), for an answer that keeps analytics but not ads. */
export function adCookieNames(cookieHeader: string): string[] {
  return ['_fbp', ...trackingCookieNames(cookieHeader).filter((name) => name === '_fbp' || name.startsWith('_gcl_'))]
}

export function eraseTrackingCookies(
  doc: ConsentDocument,
  hostname: string | undefined,
  scope: 'all' | 'ads' = 'all',
) {
  const names = new Set<string>(
    scope === 'ads' ? adCookieNames(doc.cookie) : [...OPT_OUT_COOKIE_NAMES, ...trackingCookieNames(doc.cookie)],
  )
  const domains: Array<string | null> = [null, CONSENT_SITE_DOMAIN]
  if (hostname && hostname !== 'localhost' && !hostname.endsWith('ryan-realty.com')) domains.push(hostname)
  for (const name of names) {
    for (const domain of domains) {
      const domainAttr = domain ? `; domain=${domain}` : ''
      doc.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}`
    }
  }
}

/** Stop GA4, Clarity and the ad tags now and delete their cookies. */
export function applyConsentOptOut(win: ConsentWindow, doc: ConsentDocument) {
  try {
    win.dispatchEvent?.(new CustomEvent(CLARITY_ANALYTICS_OFF_EVENT))
  } catch {
    /* no CustomEvent */
  }
  sendConsentMode(win, CONSENT_MODE_DENIED)
  sendClarity(win, { analytics: false, marketing: false })
  eraseTrackingCookies(doc, win.location?.hostname)
  stopGoogleTag(win)
}

/**
 * After the denied update has gone out, nothing more reaches Google from this
 * page: no page_view on a later client-side navigation, no event, no cookieless
 * ping. Runs last so the consent update itself is still delivered.
 */
export function stopGoogleTag(win: ConsentWindow) {
  const w = win as ConsentWindow & Record<string, unknown>
  w[`ga-disable-${GA4_ID}`] = true
  const noop = () => undefined
  w.gtag = noop
  if (!Array.isArray(w.dataLayer)) w.dataLayer = []
  try {
    Object.defineProperty(w.dataLayer, 'push', { value: () => (w.dataLayer as unknown[]).length, writable: true, configurable: true })
  } catch {
    /* frozen dataLayer */
  }
}

/**
 * Tell Google and Clarity what the visitor allowed. With ads off, any Meta or
 * Google Ads cookie left from an earlier yes is deleted too.
 */
export function applyConsentGrant(
  win: ConsentWindow,
  choice: { analytics: boolean; marketing: boolean },
  doc?: ConsentDocument,
) {
  sendConsentMode(win, consentModeUpdate(choice))
  sendClarity(win, choice)
  if (!choice.marketing && doc) eraseTrackingCookies(doc, win.location?.hostname, 'ads')
}
